import React from "react";
import PropTypes from "prop-types";
import { Typography } from "@mui/material";

const availableVariants = [
  "default",
  "heroTitle",
  "heroDescription",
  "heroNumberLabel",
  "heroNumber",
  "academyCardTitle",
  "courseCarouselTitle",
  "courseCardTitle",
  "courseCardDuration",
  "courseCardHoverTitle",
  "courseCardHoverDescription",
  "detailsPageHeading",
  "detailsTotal",
  "detailsTotalDescription",
  "body1",
  "body2",
  "body1bold",
  "body2bold",
  "progressBarLabel",
  "textLabel",
  "textValue",
  "cardSectionHeading",
  "tableHeading",
];

const variantsToComponents = {
  heroTitle: "h1",
  heroDescription: "p",
  siteLogo: "div",
  globalHeader: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  h4: "h4",
  div: "div",
  body1: "div",
  body1bold: "div",
  body2: "div",
  body2bold: "div",
  siteTitle: "div",
  cardSectionHeading: "div",
  textLink: "span",
  rightPanelLabel: "div",
  textLabel: "div",
  textValue: "div",
  trendingRanking: "div",
  trendingJobsSubHeader: "div",
  bannerHelp: "div",
  overline: "div",
  overflowMenuOption: "div",
  notificationContainerHeader: "div",
  trendingJobsHeader: "div",
  assessmentSummaryPrimary: "div",
  default: "div",
  cardTitle: "div",
  logo: "span",
  bold: "span",
  academyTitle: "h1",
  tableHeading: "span",
  detailsPageHeading: "h2",
  courseCarouselTitle: "h2",
};

const SFTypography = React.forwardRef(
  (
    {
      component = null,
      variant = "default",
      children,
      className,
      sx = {},
      noWrap = false,
      align = "inherit",
      color = null,
      italic = false,
      hasHtml = true,
      maxLines = false,
    },
    ref
  ) => {
    //added this to tranform string containing html into actually html
    if (hasHtml && typeof children === "string") {
      return (
        <Typography
          ref={ref}
          noWrap={noWrap}
          align={align}
          className={className}
          variant={variant}
          dangerouslySetInnerHTML={{ __html: children }}
          component={component || variantsToComponents[variant]}
          sx={{
            ...sx,
            ...(color && { color: `${color}.main` }),
            ...(italic && { fontStyle: "italic" }),
            ...(maxLines !== false && {
              overflow: "hidden",
              wordWrap: "break-word",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: `calc(${maxLines})`,
              lineClamp: `calc(${maxLines})`,
              WebkitBoxOrient: "vertical",
            }),
          }}
        />
      );
    }

    const RenderIfInvalidVariant = () =>
      availableVariants.indexOf(variant) == -1 && (
        <span
          style={{
            fontSize: 11,
            color: "white",
            backgroundColor: "red",
            padding: "2px",
          }}
        >
          {variant}
        </span>
      );

    return (
      <Typography
        ref={ref}
        noWrap={noWrap}
        align={align}
        className={className}
        variant={variant}
        component={component || variantsToComponents[variant]}
        sx={{
          ...sx,
          ...(color && { color: `${color}.main` }),
          ...(italic && { fontStyle: "italic" }),
          ...(maxLines !== false && {
            overflow: "hidden",
            wordWrap: "break-word",
            textOverflow: "ellipsis",
            display: "-webkit-box",
            WebkitLineClamp: `calc(${maxLines})`,
            lineClamp: `calc(${maxLines})`,
            WebkitBoxOrient: "vertical",
          }),
        }}
      >
        {children} <RenderIfInvalidVariant />
      </Typography>
    );
  }
);

SFTypography.propTypes = {
  /* The text content to be displayed  */
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  sx: PropTypes.object,
  /* The variant declared in the theme */
  variant: PropTypes.oneOf([
    "heroTitle",
    "heroDescription",
    "heroNumberLabel",
    "heroNumber",
    "academyCardTitle",
    "courseCarouselTitle",
    "courseCardTitle",
    "courseCardDuration",
    "courseCardHoverTitle",
    "courseCardHoverDescription",
    "detailsPageHeading",
    "detailsTotal",
    "detailsTotalDescription",
    "body2",
    "body1",
    "body2bold",
    "body1bold",
    "progressBarLabel",
    "textLabel",
    "textValue",
    "cardSectionHeading",
    "tableHeading",
  ]),
  /*The html element that will be rendered*/
  component: PropTypes.oneOf(["div", "span", "p", "h1", "h2", "h3", "h4"]),
  noWrap: PropTypes.bool,
  align: PropTypes.oneOf(["center", "inherit", "justify", "left", "right"]),
  hasHtml: PropTypes.bool,
  italic: PropTypes.bool,
};

export default SFTypography;
